import { Spin } from 'antd'
import { useEffect, useState } from 'react'

interface HtmlSandboxViewerProps {
  /** Signed URL to the stored .html file. */
  url: string
  title?: string
  className?: string
  style?: React.CSSProperties
}

/**
 * Content-Security-Policy injected into every previewed document. It allows the
 * inline scripts/styles our self-contained reports rely on, but blocks ALL
 * network egress (`connect-src 'none'`), external resource loads
 * (`default-src 'none'`), and form submissions (`form-action 'none'`). Combined
 * with the iframe sandbox, this makes the document effectively inert: it can run
 * its own interactivity but cannot phone home, exfiltrate, or load anything
 * external.
 */
const SANDBOX_CSP = [
  "default-src 'none'",
  "script-src 'unsafe-inline'",
  "style-src 'unsafe-inline'",
  'img-src data: blob:',
  'font-src data:',
  "connect-src 'none'",
  "form-action 'none'",
  "base-uri 'none'",
].join('; ')

/**
 * Inject the CSP as the first thing inside <head> so it applies to the whole
 * document. Falls back to creating a <head>, or prepending, when the uploaded
 * markup is malformed.
 */
function withInjectedCsp(html: string): string {
  const meta = `<meta http-equiv="Content-Security-Policy" content="${SANDBOX_CSP}">`
  if (/<head[^>]*>/i.test(html)) {
    return html.replace(/<head[^>]*>/i, (match) => `${match}${meta}`)
  }
  if (/<html[^>]*>/i.test(html)) {
    return html.replace(
      /<html[^>]*>/i,
      (match) => `${match}<head>${meta}</head>`
    )
  }
  return `${meta}${html}`
}

/**
 * Renders an uploaded HTML file inside a sandboxed iframe.
 *
 * The signed URL is served with an octet-stream content type (and would
 * download rather than render), so we fetch the bytes and re-wrap them as a
 * `text/html` blob URL. The iframe is sandboxed with `allow-scripts` only —
 * deliberately WITHOUT `allow-same-origin` — so the document gets an opaque
 * origin: its scripts run (our reports are interactive) but it cannot reach the
 * parent app, its cookies, or storage.
 */
export function HtmlSandboxViewer({
  url,
  title = 'HTML preview',
  className,
  style,
}: HtmlSandboxViewerProps) {
  const [blobUrl, setBlobUrl] = useState<string | undefined>()
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    let cancelled = false
    let objectUrl: string | undefined
    setBlobUrl(undefined)
    setHasError(false)

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load file (${res.status})`)
        return res.text()
      })
      .then((html) => {
        if (cancelled) return
        const hardened = withInjectedCsp(html)
        objectUrl = URL.createObjectURL(
          new Blob([hardened], { type: 'text/html' })
        )
        setBlobUrl(objectUrl)
      })
      .catch(() => {
        if (!cancelled) setHasError(true)
      })

    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [url])

  if (hasError) {
    return (
      <div
        className={className}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          width: '100%',
          ...style,
        }}>
        Unable to load HTML preview.
      </div>
    )
  }

  if (!blobUrl) {
    return (
      <div
        className={className}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          width: '100%',
          ...style,
        }}>
        <Spin />
      </div>
    )
  }

  return (
    <iframe
      title={title}
      src={blobUrl}
      sandbox='allow-scripts'
      className={className}
      style={{
        width: '100%',
        height: '100%',
        border: 'none',
        background: '#fff',
        ...style,
      }}
    />
  )
}
